import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../assets/css/marketplace.css";
import logo from "../../assets/img/logo_icon.png"
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import MarketplaceService from "../../services/api/Marketplace/index";
import { APP_VERSION, validEmailRegex } from "../../settings/shared/constants";
import { getYear } from "../../settings/shared/commonMethods";


const STEPS = [
    { id: 1, label: "Account details" },
    { id: 2, label: "Mail setup", optional: true },
    { id: 3, label: "Review & get your login" },
];
const MAIL_PRESETS = {
    Gmail: { host: "smtp.gmail.com", port: "587", encryption: "TLS" },
    Outlook: { host: "smtp.office365.com", port: "587", encryption: "TLS" },
    Zoho: { host: "smtp.zoho.com", port: "587", encryption: "TLS" },
};
const EMPTY_ACCOUNT = { firstName: "", lastName: "", email: "", accountName: "", description: "", address: "", url: "", city: "" };
const EMPTY_MAIL = { host: "", port: "", encryption: "TLS", username: "", password: "", senderName: "", replyTo: "" };

const mailFilled = (m) => ["host", "port", "username", "password", "senderName", "replyTo"].some((k) => m[k].trim());

const validateAccount = (a) => {
    const e = {};

    if (!a.firstName.trim()) e.firstName = "Enter your first name";
    if (!a.lastName.trim()) e.lastName = "Enter your last name";

    if (!a.email.trim()) {
        e.email = "Enter your email";
    } else if (!validEmailRegex.test(a.email.trim())) {
        e.email = "Enter a valid email, like name@company.com";
    }

    if (!a.accountName.trim()) e.accountName = "Enter an account name";
    return e;
};

const validateMail = (m) => {
    const e = {};
    if (!mailFilled(m)) return e; // all empty = skipped
    if (!m.host.trim()) e.host = "Enter the SMTP host";
    if (!m.port.trim()) e.port = "Enter the port";
    else if (!/^\d{1,5}$/.test(m.port.trim())) e.port = "Use numbers only";
    if (!m.username.trim()) e.username = "Enter the username";
    if (!m.password) e.password = "Enter the password";
    if (m.replyTo.trim() && !validEmailRegex.test(m.replyTo.trim())) e.replyTo = "Enter a valid email";
    return e;
};

const Icon = ({ children, size = 18, stroke = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const ArrowIcon = () => <Icon><path d="M5 12h14M13 6l6 6-6 6" /></Icon>;
const CheckIcon = ({ size }) => <Icon size={size}><path d="M5 12l5 5 9-10" /></Icon>;
const CopyIcon = () => <Icon size={16}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 012-2h9" /></Icon>;
const LockIcon = () => <Icon><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></Icon>;
const AlertIcon = () => <Icon><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></Icon>;
const UserIcon = () => <Icon size={18}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" /></Icon>;
const MailIcon = () => <Icon size={18}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></Icon>;

const Field = ({ id, label, required, optional, error, hint, note, className = "", children }) => (
    <div className={`mk-field ${className}`}>
        <label htmlFor={id}>{label}{required && <span className="mk-req"> *</span>}</label>
        {children}
        {error ? <small className="mk-error" role="alert">{error}</small> : hint && <small>{hint}</small>}
        {note && <div className="mk-note"><AlertIcon /><span>{note}</span></div>}
    </div>
);

const InputIcon = ({ icon, children }) => (
    <div className="mk-input-icon">
        <span className="mk-icon" aria-hidden="true">{icon}</span>
        {children}
    </div>
);

const Why = ({ title, items }) => (
    <div className="mk-card mk-side-card">
        <div className="mk-side-title">{title}</div>
        {items.map((t, i) => (
            <div className="mk-why" key={i}><span className="mk-n">{i + 1}</span><span>{t}</span></div>
        ))}
    </div>
);

const Row = ({ label, value }) => (
    <div className="mk-r"><span>{label}</span><b>{value || <em>Not added</em>}</b></div>
);

const Marketplace = () => {
    const navigate = useNavigate();
    const pageRef = useRef(null);
    const [step, setStep] = useState(1);
    const [account, setAccount] = useState(EMPTY_ACCOUNT);
    const [mail, setMail] = useState(EMPTY_MAIL);
    const [errors, setErrors] = useState({});
    const [preset, setPreset] = useState("Custom");
    const [saving, setSaving] = useState(false);
    const [credentials, setCredentials] = useState(null);
    const [copied, setCopied] = useState(null);

    useEffect(() => { pageRef.current?.scrollTo({ top: 0 }); }, [step]);

    const bind = (state, setState, key) => ({
        id: `mk-${key}`,
        value: state[key],
        className: errors[key] ? "is-invalid" : "",
        onChange: (e) => {
            setState({ ...state, [key]: e.target.value });
            if (errors[key]) setErrors({ ...errors, [key]: undefined });
        },
    });

    const goTo = (n) => { setErrors({}); setStep(n); };
    const nextFromAccount = () => {
        const e = validateAccount(account);
        setErrors(e);
        if (!Object.keys(e).length) goTo(2);
    };
    const nextFromMail = () => {
        const e = validateMail(mail);
        setErrors(e);
        if (!Object.keys(e).length) goTo(3);
    };
    const skipMail = () => {
        setMail(EMPTY_MAIL);
        setPreset("Custom");
        goTo(3);
    };
    const applyPreset = (name) => {
        setPreset(name);
        if (MAIL_PRESETS[name]) setMail({ ...mail, ...MAIL_PRESETS[name] });
    };
    const sendTest = async () => {
        try {
            await MarketplaceService.sendTestEmail(mail);
        } catch (err) {
            showNotificationGlobal("Test email will be available once the backend is connected.", "info");
        }
    };
    const save = async () => {
        setSaving(true);
        try {
            const token = new URLSearchParams(window.location.search).get("token");
            const response = await MarketplaceService.createAccount({ ...account, verifyToken: token || null, mail: mailFilled(mail) ? mail : null });
            const res = response?.data ?? response;
            if (res?.status !== "SUCCESS") {
                showNotificationGlobal(res?.errorMessage || "Could not create the account. Try again.", "error");
                return;
            }
            setCredentials({ username: res.username, password: res.password });
            setStep(4);
        } catch (err) {
            console.error("createAccount failed:", err);
            showNotificationGlobal(err?.response?.data?.errorMessage || err?.errorMessage || "Could not create the account. Try again.", "error");
        } finally {
            setSaving(false);
        }
    };
    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied((c) => (c === label ? null : c)), 2000);
        } catch (err) {
            showNotificationGlobal("Could not copy. Select the text and copy it manually.", "error");
        }
    };
    const signIn = (state) => navigate("/auth/login", { state });

    const fullName = `${account.firstName} ${account.lastName}`.trim();
    const initials = (account.accountName.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2) || "SA").toUpperCase();
    const hasMail = mailFilled(mail);

    const renderFooter = (onBack, onNext, nextLabel, loading, onSkip) => (
        <div className="mk-footer">
            {onBack ? <button type="button" className="mk-back" onClick={onBack}>← Back</button> : <span className="mk-muted">Fields marked <span className="mk-req">*</span> are required.</span>}
            <div className="mk-footer-actions">
                {onSkip && <button type="button" className="mk-skip" onClick={onSkip}>Skip for now</button>}
                <button type="button" className="mk-primary" onClick={onNext} disabled={loading}>
                    {loading ? "Saving..." : nextLabel}
                    {nextLabel.startsWith("Save") ? <CheckIcon /> : <ArrowIcon />}
                </button>
            </div>
        </div>
    );

    const renderAccount = () => (
        <>
            <section className="mk-card mk-form">
                <div>
                    <h1>Let’s set up your account</h1>
                    <p className="mk-muted">Takes about a minute. You can change everything later.</p>
                </div>
                <div className="mk-sec">About you</div>
                <div className="mk-row">
                    <Field id="mk-firstName" label="First name" required error={errors.firstName}>
                        <InputIcon icon={<UserIcon />}>
                            <input placeholder="First Name" {...bind(account, setAccount, "firstName")} autoComplete="given-name" />
                        </InputIcon>
                    </Field>
                    <Field id="mk-lastName" label="Last name" required error={errors.lastName}>
                        <InputIcon icon={<UserIcon />}>
                            <input placeholder="Last Name" {...bind(account, setAccount, "lastName")} autoComplete="family-name" />
                        </InputIcon>
                    </Field>
                </div>
                <Field id="mk-email" label="Email" required error={errors.email} note="Use a valid email. It's your username for signing in, and we'll use it to send you notifications.">
                    <InputIcon icon={<MailIcon />}>
                        <input type="email" placeholder="Email" {...bind(account, setAccount, "email")} autoComplete="email" />
                    </InputIcon>
                </Field>
                <div className="mk-sec">Your account</div>
                <Field id="mk-accountName" label="Account name" required error={errors.accountName}><input {...bind(account, setAccount, "accountName")} placeholder="Account Name" /></Field>
                <Field id="mk-description" label="Description" optional><textarea rows="3" placeholder="What will this account be used for?" {...bind(account, setAccount, "description")} /></Field>
                <div className="mk-sec">More details · optional</div>
                <Field id="mk-address" label="Office address" optional><input placeholder="Street, area" {...bind(account, setAccount, "address")} /></Field>
                <div className="mk-row">
                    <Field id="mk-url" label="Website" optional><input placeholder="https://yourcompany.com" {...bind(account, setAccount, "url")} /></Field>
                    <Field id="mk-city" label="City" optional><input placeholder="Chennai" {...bind(account, setAccount, "city")} /></Field>
                </div>
                {renderFooter(null, nextFromAccount, "Next: Mail setup")}
            </section>
            <aside className="mk-aside">
                <div className="mk-card mk-preview">
                    <div className="mk-banner" />
                    <div className="mk-preview-body">
                        <div className="mk-avatar">{initials}</div>
                        <div className="mk-kicker">Live preview</div>
                        <div className="mk-preview-name">{account.accountName || "Your account name"}</div>
                        <div className="mk-muted">{fullName || "Your name"}</div>
                        <div className="mk-chip"><span>Username</span><b>{account.email || "name@company.com"}</b></div>
                    </div>
                </div>
                <Why title="Why we ask for these details" items={[
                    "Your email becomes your username for signing in, and we'll use it for notifications.",
                    "Your account name is how this workspace is identified.",
                    "Not sure about the optional details? Skip them, you can add them anytime.",
                ]} />
            </aside>
        </>
    );

    const renderMail = () => (
        <>
            <section className="mk-card mk-form">
                <div>
                    <h1>Send emails from your own address</h1>
                    <p className="mk-muted">Optional. Leave it empty and press Next to skip, and add it later in Settings.</p>
                </div>
                <div className="mk-field">
                    <span className="mk-lb">Quick fill</span>
                    <div className="mk-chips">
                        {["Gmail", "Outlook", "Zoho", "Custom"].map((p) => (
                            <button type="button" key={p} className="mk-pill" aria-pressed={preset === p} onClick={() => applyPreset(p)}>{p}</button>
                        ))}
                    </div>
                </div>
                <div className="mk-sec">Server</div>
                <div className="mk-row">
                    <Field id="mk-host" label="SMTP host" className="mk-grow" error={errors.host}><input {...bind(mail, setMail, "host")} /></Field>
                    <Field id="mk-port" label="Port" className="mk-port" error={errors.port}><input inputMode="numeric" {...bind(mail, setMail, "port")} /></Field>
                </div>
                <div className="mk-field">
                    <span className="mk-lb" id="mk-enc-label">Encryption</span>
                    <div className="mk-seg" role="group" aria-labelledby="mk-enc-label">
                        {["None", "TLS", "SSL"].map((o) => (
                            <button type="button" key={o} aria-pressed={mail.encryption === o} onClick={() => setMail({ ...mail, encryption: o })}>{o}</button>
                        ))}
                    </div>
                </div>
                <div className="mk-sec">Sign in to the mail server</div>
                <div className="mk-row">
                    <Field id="mk-username" label="Username" error={errors.username}><input autoComplete="off" {...bind(mail, setMail, "username")} /></Field>
                    <Field id="mk-password" label="Password" error={errors.password}><input type="password" autoComplete="new-password" {...bind(mail, setMail, "password")} /></Field>
                </div>
                <div className="mk-sec">How emails look</div>
                <div className="mk-row">
                    <Field id="mk-senderName" label="Sender name"><input {...bind(mail, setMail, "senderName")} /></Field>
                    <Field id="mk-replyTo" label="Reply-to email" error={errors.replyTo}><input type="email" placeholder="support@yourcompany.com" {...bind(mail, setMail, "replyTo")} /></Field>
                </div>
                <div className="mk-test">
                    <button type="button" className="mk-outline" onClick={sendTest}>Send test email</button>
                    <span className="mk-muted">Not tested yet. We’ll send a test message to <b>{account.email}</b>.</span>
                </div>
                {renderFooter(() => goTo(1), nextFromMail, "Next: Review", false, skipMail)}
            </section>
            <aside className="mk-aside">
                <div className="mk-card mk-side-card">
                    <div className="mk-recap-head"><span className="mk-side-title">✓ Account details</span><button type="button" className="mk-link" onClick={() => goTo(1)}>Edit</button></div>
                    <div className="mk-recap"><b>{account.accountName}</b><br /><span className="mk-muted">{fullName} · {account.email}</span></div>
                </div>
                <Why title="Why set up mail?" items={[
                    "Alerts and notifications go out from your own address.",
                    "Replies reach the right inbox.",
                    "Not sure about the details? Skip it, nothing breaks.",
                ]} />
            </aside>
        </>
    );

    const renderReview = () => (
        <>
            <section className="mk-card mk-form">
                <div>
                    <h1>Everything look right?</h1>
                    <p className="mk-muted">Check your details, then save. You can edit any of this later.</p>
                </div>
                <div className="mk-box">
                    <div className="mk-hd"><b>Account details</b><button type="button" className="mk-link" onClick={() => goTo(1)}>Edit</button></div>
                    <Row label="Account name" value={account.accountName} />
                    <Row label="Name" value={fullName} />
                    <Row label="Email (username)" value={account.email} />
                    <Row label="Description" value={account.description} />
                    <Row label="Office address" value={account.address} />
                    <Row label="Website" value={account.url} />
                    <Row label="City" value={account.city} />
                </div>
                <div className="mk-box">
                    <div className="mk-hd">
                        <b>Mail setup <span className={`mk-tag ${hasMail ? "ok" : ""}`}>{hasMail ? "Configured" : "Not configured"}</span></b>
                        <button type="button" className="mk-link" onClick={() => goTo(2)}>Edit</button>
                    </div>
                    {hasMail ? (
                        <>
                            <Row label="Server" value={`${mail.host} : ${mail.port}`} />
                            <Row label="Encryption" value={mail.encryption} />
                            <Row label="Username" value={mail.username} />
                            <Row label="Password" value={mail.password ? "••••••••••••" : ""} />
                            <Row label="Sender name" value={mail.senderName} />
                            <Row label="Reply-to" value={mail.replyTo} />
                        </>
                    ) : (
                        <div className="mk-r"><span>You can add this later in Settings.</span></div>
                    )}
                </div>
                {renderFooter(() => goTo(2), save, "Save and create account", saving)}
            </section>
            <aside className="mk-aside">
                <div className="mk-card mk-login-preview">
                    <div className="mk-banner mk-banner--thin" />
                    <div className="mk-preview-body">
                        <div className="mk-side-title">Your login, after you save</div>
                        <div className="mk-chip"><span>Username</span><b>{account.email}</b></div>
                        <div className="mk-chip mk-chip--icon"><LockIcon /><span><span>Password</span><br /><b>Shown once you save</b></span></div>
                        <p className="mk-muted">Copy your password straight away. You’ll use it with your username to sign in.</p>
                    </div>
                </div>
            </aside>
        </>
    );

    const renderSuccess = () => (
        <main className="mk-card mk-success">
            <div className="mk-success-top">
                <div className="mk-success-check"><CheckIcon size={32} /></div>
                <h1>Your account is ready</h1>
                <p><b>{account.accountName}</b> has been created.</p>
            </div>
            <div className="mk-success-body">
                <div className="mk-box">
                    <div className="mk-side-title">Your login</div>
                    {[["Username", credentials?.username], ["Password", credentials?.password]].map(([label, value]) => (
                        <div className="mk-cred" key={label}>
                            <div><small>{label}</small><b>{value}</b></div>
                            <button type="button" className={`mk-outline mk-copy ${copied === label ? "is-copied" : ""}`} onClick={() => copy(value, label)}>
                                {copied === label ? <><CheckIcon size={16} />Copied</> : <><CopyIcon />Copy</>}
                            </button>
                        </div>
                    ))}
                    <div className="mk-warn"><AlertIcon /><span>Copy your password and keep it somewhere safe. You’ll need it to sign in.</span></div>
                </div>
                <div className="mk-why"><span className="mk-n">1</span><span>Copy your username and password</span></div>
                <div className="mk-why"><span className="mk-n">2</span><span>Sign in with your email and password</span></div>
                <button type="button" className="mk-primary mk-wide" onClick={() => signIn({ showLoginForm: true, loginMethod: "password" })}>Continue to sign in <ArrowIcon /></button>
            </div>
        </main>
    );

    return (
        <div className="mk-page" ref={pageRef}>
            <div className="mk-shell">
                <header className="mk-header">
                    <div className="mk-brand"><img src={logo} alt="" />SUPERAGENTX</div>
                </header>
                {step < 4 && (
                    <nav aria-label="Progress" className="mk-progress">
                        <ol>
                            {STEPS.map((s) => (
                                <li key={s.id} aria-current={step === s.id ? "step" : undefined}>
                                    <div className={`mk-bar ${step >= s.id ? "on" : ""}`} />
                                    <div className={`mk-step ${step === s.id ? "active" : ""} ${step > s.id ? "done" : ""}`}>
                                        {step > s.id ? `✓ ${s.label}` : `${s.id} · ${s.label}`}
                                        {s.optional && <span className="mk-tag">Optional</span>}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </nav>
                )}
                {step === 4 ? renderSuccess() : (
                    <main className="mk-main">
                        {step === 1 && renderAccount()}
                        {step === 2 && renderMail()}
                        {step === 3 && renderReview()}
                    </main>
                )}
                <footer className="mk-powered">© {getYear()} SuperAgentX • Version {APP_VERSION}</footer>
            </div>
        </div>
    );
};

export default Marketplace;